import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const ProcessStep = ({ number, title, description, image }) => (
  <div className="relative flex flex-col lg:flex-row items-start gap-16 lg:gap-24">
    {/* Content */}
    <div className="flex-1 relative">
      <div className="space-y-6">
        <div className="text-sm text-neutral-600">
          Step {number}
        </div>
        <h3 className="text-4xl font-light text-neutral-800 leading-tight">
          {title}
        </h3>
        <p className="text-lg text-neutral-600 leading-relaxed max-w-xl">
          {description}
        </p>
      </div>
    </div>

    {/* Image with Decorative Circles */}
    <div className="flex-1 relative">
      <div className="relative">
        {/* Decorative Circles */}
        <div className="absolute -right-12 -top-12 w-[400px] h-[400px] border-2 border-[#64D652] rounded-full opacity-20" />
        <div className="absolute -right-24 -top-24 w-[500px] h-[500px] border-2 border-[#64D652] rounded-full opacity-10" />
        
        {/* Image Container */}
        <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
          <img 
            src={image} 
            alt={title}
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  </div>
);

const HowItWorksSection = () => {
  useEffect(() => {
    AOS.init({
      duration: 1200,
      once: true,
      mirror: false,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
    });
  }, []);

  const steps = [
    {
      number: "1",
      title: "Initial Consultation & Assessment",
      description: "Begin with a comprehensive wellness assessment using our AI-powered diagnostic tools. Our experts analyze your unique health profile, lifestyle, and goals to create your personalized wellness blueprint.",
      image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80"
    },
    {
      number: "2",
      title: "Personalized Program Design",
      description: "Based on your assessment, we craft a bespoke wellness program that harmoniously combines traditional healing practices with cutting-edge therapeutic approaches, tailored precisely to your needs.",
      image: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80"
    },
    {
      number: "3",
      title: "Expert-Led Sessions",
      description: "Experience premium wellness sessions in the comfort of your home, guided by our certified practitioners. Each session is meticulously designed using state-of-the-art techniques and ancient wisdom.",
      image: "https://images.unsplash.com/photo-1598259812920-0f409c9d3f14?auto=format&fit=crop&q=80"
    },
    {
      number: "4",
      title: "Progress Tracking & Optimization",
      description: "Monitor your wellness journey through our advanced tracking system. Our AI continuously analyzes your progress, making real-time adjustments to optimize your results and ensure sustained transformation.",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&q=80"
    }
  ];

  return (
    <section className="relative py-24 bg-[#e8e3df] overflow-hidden">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <div className="text-center mb-24" data-aos="fade-up">
          <div className="text-sm text-neutral-600 mb-4">
            Your Journey to Wellness
          </div>
          <h2 className="text-6xl font-light mb-6 text-neutral-800">
            How Vedastik Works
          </h2>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            Experience a seamless journey to optimal wellness through our innovative
            four-step process, combining luxury with effectiveness.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-32">
          {steps.map((step, index) => (
            <div key={index} data-aos="fade-up">
              <ProcessStep {...step} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection; 